// Kinio KMS 操作台的 mock（只在 dev / test 打開，mockMode.ts）：kmgr 沒部署時（`all`）或畫面先做、kmgr 還沒有的端點（`missing`，
// api.ts MISSING_ENDPOINTS，目前是空的）讓畫面能從頭走一遍：總覽啟用卡 → 精靈 → 啟用 → 總覽出現事故 → 事故頁〔我來處理〕→ 輪值表。
// 回 kmgr 的原始形狀（wire.ts，照 cloud/kmgr api/openapi.json），跟真的 kmgr 走同一個轉換；M1 的事故只有主機的兩種（服務停止、主機離線）。
// 狀態放這個瀏覽器的 localStorage（重新整理還在）。錯誤照 SPEC #eng-10：KinioError(status, code, message, detail)。
import { KinioError } from '@kinio/web-sdk';
import type { CreatedDeployToken, DeployToken, Enrollment, EnrollReview, EnrollReviewResult, HomeNotify, OncallSchedule, ProbeInput, SiteInfo, Thresholds, TimeWindows } from './types';
import type {
  WireAlert,
  WireBusinessHours,
  WireDashboard,
  WireHistory,
  WireHomeKmgr,
  WireHomeNetwork,
  WireHomeStatus,
  WireHost,
  WireHostList,
  WireIncident,
  WireIncidentDetail,
  WireMaintenance,
  WireNames,
  WireNotification,
  WireOrgKmgr,
  WireOrgSeats,
  WireProbe,
  WireSiteInfo,
  WireTargetList,
  WireThresholds,
} from './wire';
import { EMPTY_AGENTS, EMPTY_COUNTS } from './wire';
import { defaultThresholds, kmgrScopeOf, overriddenRules } from './model';

export interface MockWorld {
  me: string;
  meName?: string;
  orgs: { org_id: string; display_name: string; role?: string; kind?: string }[];
  homes: { home_id: string; display_name: string; org_id?: string; role?: string }[];
}

interface MockIncident {
  inc: WireIncident;
  alerts: WireAlert[];
  history: WireHistory[];
  notifications: WireNotification[];
}

/** 情境開關（localStorage `vms.kmgr.mock.flags`，JSON）：collector_offline = 唯一的收集器離線（KMS-25 讀不到）；global_stop = Kinio 全域關閉紅條。 */
export interface MockFlags {
  collector_offline?: boolean;
  global_stop?: boolean;
}

interface MockState {
  dpa: Record<string, { version: string; by: string; at: string }>;
  enabled: Record<string, boolean>;
  incidents: MockIncident[];
  schedules: OncallSchedule[];
  notify: Record<string, { schedule_id: string | null; customer_contact: string | null }>;
  tokens: (DeployToken & { created_ms: number })[];
  /** token_id → 預期清單外的報到（待確認，M5-05）；舊的 localStorage 沒有這欄 */
  reviews?: Record<string, EnrollReview[]>;
  /** 每台主機改過的設定（關鍵、預期離線時段）；主機本身由家戶 id 產生 */
  targets: Record<string, { critical?: boolean; expected_offline?: TimeWindows | null }>;
  probes: Record<string, (WireProbe & { home_id: string })[]>;
  assignments: Record<string, string[]>;
  maintenance: Record<string, { until?: string; set_by?: string; set_at?: string; schedule?: TimeWindows }>;
  business: Record<string, TimeWindows>;
  thresholds: Record<string, Thresholds>;
  site: Record<string, SiteInfo>;
  /** seat_id → 指派到哪台（席次固定每組織伺服器 2、工作站 2） */
  seats: Record<string, Record<string, string | undefined>>;
  seq: number;
}

const KEY = 'vms.kmgr.mock.state.v2';
const FLAGS_KEY = 'vms.kmgr.mock.flags';
const DPA_VERSION = 'dev-1';
const OTHER = 'u-mock-other';
const THINGS: Record<string, string> = { 'KMA-MOCK-SRV01': 'SRV-ERP01', 'KMA-MOCK-SRV02': 'SRV-FILE02', 'KMA-MOCK-MON01': 'SRV-MON01' };

function empty(): MockState {
  return { dpa: {}, enabled: {}, incidents: [], schedules: [], notify: {}, tokens: [], targets: {}, probes: {}, assignments: {}, maintenance: {}, business: {}, thresholds: {}, site: {}, seats: {}, seq: 1 };
}

export function readMockFlags(): MockFlags {
  try {
    return (JSON.parse(localStorage.getItem(FLAGS_KEY) || '{}') as MockFlags) ?? {};
  } catch {
    return {};
  }
}

let state: MockState | null = null;

function load(): MockState {
  if (state) return state;
  try {
    const raw = localStorage.getItem(KEY);
    state = raw ? { ...empty(), ...(JSON.parse(raw) as MockState) } : empty();
  } catch {
    state = empty();
  }
  return state;
}

function save(): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(state));
  } catch {
    /* 無 storage */
  }
}

const iso = (msAgo: number) => new Date(Date.now() - msAgo).toISOString();
const MIN = 60_000;

function id(s: MockState, prefix: string): string {
  s.seq += 1;
  return `${prefix}-MOCK${String(s.seq).padStart(6, '0')}`;
}

/** 家戶第一次啟用時放三件示範事故（緊急服務停止未處理、緊急主機離線已恢復別人處理中、重要主機離線未處理）。 */
function seedIncidents(s: MockState, w: MockWorld, homeId: string): void {
  const h = w.homes.find((x) => x.home_id === homeId);
  if (!h || s.incidents.some((i) => i.inc.home_id === homeId)) return;
  const base = { home_id: homeId, org_id: h.org_id ?? '', recurrence_count: 1, recurring: false, affected: 1, escalation_step: 0 };
  const alert = (target: string, kind: string, sev: string, msAgo: number, subject?: string, recoveredAgo?: number): WireAlert => ({
    alert_id: id(s, 'alt'),
    target_ref: target,
    kind,
    subject,
    severity: sev,
    opened_at: iso(msAgo),
    resolved_at: recoveredAgo === undefined ? undefined : iso(recoveredAgo),
  });
  const opened = (msAgo: number): WireHistory => ({ version: 1, handling: 'open', now_state: 'ongoing', cause: 'opened', by: 'system', at: iso(msAgo) });
  const pushed = (msAgo: number, users: string[]): WireNotification => ({ key: 'kmgr_alert.opened', variant: 'opened', route: 'oncall', user_ids: users, roles: [], priority: 'time_sensitive', at: iso(msAgo) });
  s.incidents.push(
    {
      inc: { ...base, incident_id: id(s, 'inc'), kind: 'service_stopped', severity: 'p1', now_state: 'ongoing', handling: 'open', version: 1, target_ref: 'KMA-MOCK-SRV01', subject: 'postgresql', opened_at: iso(5 * MIN) },
      alerts: [alert('KMA-MOCK-SRV01', 'service_stopped', 'p1', 5 * MIN, 'postgresql')],
      history: [opened(5 * MIN)],
      notifications: [pushed(5 * MIN, [w.me])],
    },
    {
      inc: {
        ...base,
        incident_id: id(s, 'inc'),
        kind: 'host_offline',
        severity: 'p1',
        now_state: 'recovered',
        recovered_at: iso(43 * MIN),
        handling: 'claimed',
        claimed_by: OTHER,
        claimed_at: iso(60 * MIN),
        version: 2,
        recurrence_count: 3,
        recurring: true,
        target_ref: 'KMA-MOCK-SRV02',
        opened_at: iso(80 * MIN),
      },
      alerts: [alert('KMA-MOCK-SRV02', 'host_offline', 'p1', 80 * MIN, undefined, 43 * MIN)],
      history: [
        opened(80 * MIN),
        { version: 2, from: 'open', handling: 'claimed', now_state: 'ongoing', cause: 'user', by: OTHER, assignee: OTHER, at: iso(60 * MIN) },
        { version: 2, handling: 'claimed', now_state: 'recovered', cause: 'recovered', by: 'system', at: iso(43 * MIN) },
      ],
      notifications: [pushed(80 * MIN, [OTHER])],
    },
    {
      inc: { ...base, incident_id: id(s, 'inc'), kind: 'host_offline', severity: 'p2', now_state: 'ongoing', handling: 'open', version: 1, target_ref: 'KMA-MOCK-MON01', opened_at: iso(22 * MIN) },
      alerts: [alert('KMA-MOCK-MON01', 'host_offline', 'p2', 22 * MIN)],
      history: [opened(22 * MIN)],
      notifications: [],
    },
  );
}

function names(w: MockWorld): WireNames {
  return {
    homes: Object.fromEntries(w.homes.map((h) => [h.home_id, h.display_name])),
    orgs: Object.fromEntries(w.orgs.map((o) => [o.org_id, o.display_name])),
    users: { [w.me]: w.meName ?? '', [OTHER]: '王建民' },
    things: THINGS,
  };
}

function orgOfHome(w: MockWorld, homeId: string): string | undefined {
  return w.homes.find((h) => h.home_id === homeId)?.org_id;
}

function enabledHomes(s: MockState, w: MockWorld): MockWorld['homes'] {
  return w.homes.filter((h) => s.enabled[h.home_id]);
}

/** scope 可重複帶（組織多選，M5-04）：任一個範圍包含就算。 */
function inScopes(w: MockWorld, scope: string | number | string[] | undefined, homeId: string): boolean {
  const list = Array.isArray(scope) ? scope : [String(scope ?? 'all')];
  return list.some((x) => inScope(w, x, homeId));
}

function scopeLabel(scope: string | number | string[] | undefined): string {
  return Array.isArray(scope) ? scope.join(',') : String(scope ?? 'all');
}

function inScope(w: MockWorld, scope: string, homeId: string): boolean {
  const k = kmgrScopeOf(scope).scope;
  if (k === 'all') return true;
  if (k.startsWith('home:')) return k.slice(5) === homeId;
  if (k.startsWith('org:')) return orgOfHome(w, homeId) === k.slice(4);
  return true;
}

const SEV_RANK: Record<string, number> = { p1: 0, p2: 1, p3: 2 };
function stage(i: WireIncident): number {
  if (i.handling === 'resolved') return 4;
  return (i.handling === 'claimed' ? 2 : 0) + (i.now_state === 'ongoing' ? 0 : 1);
}
function sorted(list: WireIncident[]): WireIncident[] {
  return [...list].sort((a, b) => SEV_RANK[a.severity] - SEV_RANK[b.severity] || stage(a) - stage(b) || Date.parse(a.opened_at) - Date.parse(b.opened_at));
}

function counts(list: WireIncident[], me: string) {
  const open = list.filter((i) => i.handling !== 'resolved');
  const unclaimed = open.filter((i) => i.handling === 'open' || i.handling === 'acknowledged');
  return {
    ...EMPTY_COUNTS,
    total: open.length,
    p1: open.filter((i) => i.severity === 'p1').length,
    p2: open.filter((i) => i.severity === 'p2').length,
    p3: open.filter((i) => i.severity === 'p3').length,
    ongoing: open.filter((i) => i.now_state === 'ongoing').length,
    unclaimed: unclaimed.length,
    p1_unclaimed: unclaimed.filter((i) => i.severity === 'p1').length,
    mine: open.filter((i) => i.handling === 'claimed' && i.claimed_by === me).length,
  };
}

// ---- 主機 / 收集器 / 探針（每個已啟用的家戶一樣的一組假主機：家戶 id 的尾碼做 device_ref） ----

const SRV = (homeId: string, n: string) => `KMA-MOCK-${homeId.slice(-4).toUpperCase()}-${n}`;

/** 一個家戶的主機：收集器 SRV-MON01（在線）、關鍵伺服器 SRV-ERP01（在線、磁碟 91%）、SRV-FILE02（離線 2 小時）、工作站 DESKTOP-ACC07（本機已暫停）、DESKTOP-DSN05（☾ 已關機）、NB-SAL07（☾ 不在現場）、DESKTOP-MKT04（未授權）。 */
function mockHosts(s: MockState, homeId: string, orgId: string, flags: MockFlags): WireHost[] {
  const base = (n: string, hostname: string, over: Partial<WireHost>): WireHost => {
    const ref = SRV(homeId, n);
    const t = s.targets[ref] ?? {};
    return {
      target_ref: ref,
      kind: 'agent',
      org_id: orgId,
      home_id: homeId,
      hostname,
      seat_class: 'server',
      license_state: 'licensed',
      presence: 'online',
      power_state: 'on',
      state: 'active',
      last_seen_at: iso(20_000),
      os: { family: 'linux', name: 'Ubuntu', version: '24.04' },
      agent_version: '1.0.3',
      key_protection: 'hardware',
      metrics: { cpu_pct: 12, mem_pct: 48, disks: [{ mount: '/', used_pct: 55 }] },
      remote_mode: 'attended',
      can_remote: true,
      ...over,
      ...(t.critical !== undefined ? { critical: t.critical } : {}),
      ...(t.expected_offline ? { expected_offline: t.expected_offline } : {}),
    };
  };
  const win = { family: 'windows' as const, name: 'Windows 11 Pro', version: '23H2' };
  const collectorOffline = !!flags.collector_offline;
  return [
    base('MON01', 'SRV-MON01', {
      presence: collectorOffline ? 'offline' : 'online',
      last_seen_at: collectorOffline ? iso(12 * MIN) : iso(20_000),
      collector: { assignments: s.assignments[homeId] ?? ['10.20.0.0/16'], assignments_version: 3, delivered_version: collectorOffline ? 2 : 3, delivered_at: iso(3 * 3600_000) },
      metrics: { cpu_pct: 9, mem_pct: 41, disks: [{ mount: '/', used_pct: 38 }] },
    }),
    base('SRV01', 'SRV-ERP01', { critical: true, metrics: { cpu_pct: 12, mem_pct: 48, disks: [{ mount: 'D:', used_pct: 91 }, { mount: 'C:', used_pct: 40 }] }, os: { family: 'windows', name: 'Windows Server', version: '2022' } }),
    base('SRV02', 'SRV-FILE02', { presence: 'offline', last_seen_at: iso(2 * 3600_000), metrics: undefined }),
    base('WS01', 'DESKTOP-ACC07', { seat_class: 'workstation', os: win, primary_user: '王小明 · 會計部', metrics: { cpu_pct: 8, mem_pct: 62, disks: [{ mount: 'C:', used_pct: 71 }] }, local_paused: { by: '王小明', at: iso(26 * 3600_000) } }),
    base('WS02', 'DESKTOP-DSN05', { seat_class: 'workstation', os: win, primary_user: '陳怡安 · 設計部', presence: 'shutdown', power_state: 'shutdown', last_seen_at: iso(15 * 3600_000), metrics: undefined }),
    base('WS03', 'NB-SAL07', { seat_class: 'workstation', os: win, primary_user: '林佩珊 · 業務部', presence: 'expected_offline', last_seen_at: iso(2 * 86_400_000), metrics: undefined }),
    base('WS04', 'DESKTOP-OLD01', { seat_class: 'workstation', os: win, agent_version: '1.0.0', key_protection: 'software', metrics: { cpu_pct: 21, mem_pct: 81, disks: [{ mount: 'C:', used_pct: 66 }] } }),
    base('WS05', 'DESKTOP-MKT04', { seat_class: 'workstation', os: win, primary_user: '行銷部', license_state: 'unlicensed', license_reason: 'no_seat', metrics: undefined }),
  ];
}

function hostCounts(list: WireHost[]): Record<string, number> {
  const c: Record<string, number> = { all: list.length, online: 0, offline: 0, expected_offline: 0, unlicensed: 0, suspended: 0, revoked: 0 };
  for (const h of list) {
    if (h.license_state === 'unlicensed') c.unlicensed++;
    if (h.state === 'suspended') c.suspended++;
    else if (h.state === 'revoked') c.revoked++;
    else if (h.presence === 'online') c.online++;
    else if (h.presence === 'offline') c.offline++;
    else c.expected_offline++;
  }
  return c;
}

function hostGroup(h: WireHost): string {
  if (h.state === 'suspended') return 'suspended';
  if (h.state === 'revoked') return 'revoked';
  if (h.license_state === 'unlicensed') return 'unlicensed';
  if (h.presence === 'online') return 'online';
  if (h.presence === 'offline') return 'offline';
  return 'expected_offline';
}

function agentCounts(list: WireHost[]) {
  const c = hostCounts(list);
  return { total: c.all, online: c.online, offline: c.offline, expected_offline: c.expected_offline, unlicensed: c.unlicensed };
}

/** 一個家戶的探針：第一次問時放 CORE-SW（snmp）、FG-60F（ping）、ERP 網站（http）、POS 備份檔（file）。 */
function mockProbes(s: MockState, homeId: string): (WireProbe & { home_id: string })[] {
  if (!s.probes[homeId]) {
    const col = SRV(homeId, 'MON01');
    s.probes[homeId] = [
      { probe_id: id(s, 'prb'), home_id: homeId, kind: 'snmp', name: '1F 核心交換器 CORE-SW', target: '10.20.0.2', credential_ref: 'cred-mock-1', collector: col, interval_s: 60, timeout_s: 5, last_at: iso(40_000), last_up: true, snmp: { interfaces: 48, interfaces_down: 1, poe_w: 182, poe_budget_w: 370, temperature_c: 41 } },
      { probe_id: id(s, 'prb'), home_id: homeId, kind: 'ping', name: '防火牆 FG-60F', target: '10.20.0.1', collector: col, interval_s: 30, timeout_s: 3, last_at: iso(30_000), last_up: true },
      { probe_id: id(s, 'prb'), home_id: homeId, kind: 'snmp', name: '2F 交換器 SW-2F', target: '10.20.0.3', credential_ref: 'cred-mock-2', collector: col, interval_s: 60, timeout_s: 5, last_at: iso(50_000), last_up: false, last_error: 3 },
      { probe_id: id(s, 'prb'), home_id: homeId, kind: 'ping', name: '4F 交換器 SW-4F', target: '10.20.0.5', interval_s: 30, timeout_s: 3 },
      { probe_id: id(s, 'prb'), home_id: homeId, kind: 'http', name: 'ERP 網站', target: 'erp.example.internal', collector: col, http: { scheme: 'https', path: '/health', expect_status: [200] }, interval_s: 60, timeout_s: 5, last_at: iso(45_000), last_up: true },
      { probe_id: id(s, 'prb'), home_id: homeId, kind: 'file', name: 'POS 每日備份', collector: col, file: { path: '\\\\SRV-FILE02\\backup\\pos.bak' }, interval_s: 3600, timeout_s: 10, last_at: iso(2 * 3600_000), last_up: false, last_error: 7 },
    ];
    save();
  }
  return s.probes[homeId];
}

function homeNetwork(s: MockState, homeId: string, hosts: WireHost[], flags: MockFlags): WireHomeNetwork {
  const probes = mockProbes(s, homeId);
  const col = hosts.find((h) => h.collector);
  const offline = !!flags.collector_offline && col ? [{ device_ref: col.target_ref, last_seen_at: col.last_seen_at }] : [];
  const assigned = probes.filter((p) => p.collector);
  return {
    source_state: offline.length ? 'unreadable' : 'ok',
    reason: offline.length ? 'collector_offline' : undefined,
    collectors: col ? 1 : 0,
    collectors_offline: offline,
    probes: probes.length,
    probes_up: assigned.filter((p) => p.last_up).length,
    probes_failing: assigned.filter((p) => p.last_at && !p.last_up).length,
    probes_unassigned: probes.length - assigned.length,
    probes_no_result: assigned.filter((p) => !p.last_at).length,
  };
}

function maintenanceOf(s: MockState, homeId: string): WireMaintenance {
  const m = s.maintenance[homeId] ?? {};
  const now = Date.now();
  const untilMs = m.until ? Date.parse(m.until) : 0;
  const active = untilMs > now;
  return { home_id: homeId, until: m.until, set_by: m.set_by, set_at: m.set_at, schedule: m.schedule ?? { tz: 'Asia/Taipei', windows: [] }, active, active_until: active ? m.until : undefined };
}

function businessOf(s: MockState, homeId: string): WireBusinessHours {
  const b = s.business[homeId] ?? { tz: 'Asia/Taipei', windows: [] };
  return { home_id: homeId, tz: b.tz, windows: b.windows, after_hours: false };
}

function homeStatus(s: MockState, h: MockWorld['homes'][number], w: MockWorld, flags: MockFlags = readMockFlags()): WireHomeStatus {
  const incs = s.incidents.map((x) => x.inc).filter((i) => i.home_id === h.home_id);
  const ongoing = incs.filter((i) => i.now_state === 'ongoing' && i.handling !== 'resolved');
  const hosts = mockHosts(s, h.home_id, h.org_id ?? '', flags);
  const network = homeNetwork(s, h.home_id, hosts, flags);
  const maint = maintenanceOf(s, h.home_id);
  const state = maint.active ? 'maintenance' : network.source_state === 'unreadable' ? 'unreadable' : ongoing.some((i) => i.severity === 'p1') ? 'bad' : ongoing.length ? 'attention' : 'ok';
  return {
    home_id: h.home_id,
    org_id: h.org_id ?? '',
    state,
    agents: agentCounts(hosts),
    incidents: counts(incs, w.me),
    last_seen_at: iso(20_000),
    network,
    maintenance: maint.active ? { until: maint.until, set_by: maint.set_by } : undefined,
  };
}

/** 組織的席次：伺服器 3、工作站 3；指派先給已授權的主機（第一個家戶的），之後照 s.seats 改派。 */
function orgSeats(s: MockState, w: MockWorld, orgId: string, flags: MockFlags): WireOrgSeats {
  const homes = enabledHomes(s, w).filter((h) => h.org_id === orgId);
  const hosts = homes.flatMap((h) => mockHosts(s, h.home_id, orgId, flags));
  const assigned = s.seats[orgId];
  const seatIds = { server: ['seat-srv-1', 'seat-srv-2', 'seat-srv-3'], workstation: ['seat-ws-1', 'seat-ws-2', 'seat-ws-3'] };
  const mk = (cls: 'server' | 'workstation') => {
    const licensed = hosts.filter((h) => h.seat_class === cls && h.license_state === 'licensed');
    return seatIds[cls].map((seat_id, i) => {
      const ref = assigned ? assigned[seat_id] : licensed[i]?.target_ref;
      const host = hosts.find((h) => h.target_ref === ref);
      return { seat_id, class: cls, status: 'active', source: 'subscription' as const, assigned_ref: ref || undefined, assigned_at: ref ? iso(5 * 86_400_000) : undefined, home_id: host?.home_id, hostname: host?.hostname };
    });
  };
  const seats = [...mk('server'), ...mk('workstation')];
  const summary = (['server', 'workstation'] as const).map((cls) => {
    const of = seats.filter((x) => x.class === cls);
    return { class: cls, total: of.length, assigned: of.filter((x) => x.assigned_ref).length, available: of.filter((x) => !x.assigned_ref).length };
  });
  const seatRefs = new Set(seats.map((x) => x.assigned_ref).filter(Boolean));
  const unlicensed = hosts
    .filter((h) => h.license_state === 'unlicensed' || (assigned && !seatRefs.has(h.target_ref)))
    .map((h) => ({ device_ref: h.target_ref, home_id: h.home_id ?? '', seat_class: h.seat_class, reason: 'no_seat' as const, since: iso(3 * 86_400_000), hostname: h.hostname }));
  const org = w.orgs.find((o) => o.org_id === orgId);
  return { org_id: orgId, summary, seats, unlicensed, can_buy: org?.role === 'owner', names: names(w) };
}

function notFound(): never {
  throw new KinioError(404, 'NOT_FOUND', '找不到');
}

function tokenView(t: MockState['tokens'][number]): DeployToken {
  // 假的報到進度：建立後每 5 秒多一台，最多到上限；每 7 台有 1 台失敗
  const n = Math.min(t.max_count, Math.floor((Date.now() - t.created_ms) / 5000));
  const failed = Math.floor(n / 7);
  const approved = n - failed;
  const { created_ms: _c, ...rest } = t;
  const state = t.revoked_at ? 'revoked' : Date.parse(t.expires_at) < Date.now() ? 'expired' : approved >= t.max_count ? 'exhausted' : 'active';
  const pending = state === 'active' ? (load().reviews?.[t.token_id] ?? []).filter((r) => r.state === 'pending').length : 0;
  return { ...rest, state, used_count: approved, progress: { approved, online: approved, failed, remaining: Math.max(0, t.max_count - approved), pending } };
}

/** 有預期清單的權杖：一台不在清單、一台跟清單第一台同名（已報到）→ 待確認（M5-05）。 */
function seedReviews(t: { token_id: string; expected_hosts?: string[] }): EnrollReview[] {
  const first = t.expected_hosts?.[0];
  if (!first) return [];
  const at = (m: number) => new Date(Date.now() - m * MIN).toISOString();
  const os = { family: 'windows', name: 'Windows 11 Pro', version: '23H2' };
  return [
    { review_id: `ker-${t.token_id}-1`, hostname: 'LAPTOP-HOME-07', os, source_ip: '114.32.18.9', reason: 'not_listed', first_at: at(42), last_at: at(12), state: 'pending' },
    { review_id: `ker-${t.token_id}-2`, hostname: first, os, source_ip: '203.69.141.36', reason: 'duplicate_name', first_at: at(18), last_at: at(3), state: 'pending' },
  ];
}

function decideReviews(t: MockState['tokens'][number], list: EnrollReview[], body: { decision: 'accept' | 'reject'; review_ids: string[] }): { results: EnrollReviewResult[] } {
  const now = new Date().toISOString();
  const results = body.review_ids.map((id): EnrollReviewResult => {
    const r = list.find((x) => x.review_id === id);
    if (!r) return { review_id: id, error: 'NOT_FOUND' };
    if (r.state !== 'pending') return { review_id: id, state: r.state, error: 'CONFLICT', detail: { state: r.state } };
    r.state = body.decision === 'accept' ? 'accepted' : 'rejected';
    r.decided_at = now;
    if (r.state === 'accepted' && r.hostname && !(t.expected_hosts ?? []).some((h) => h.toLowerCase() === r.hostname.toLowerCase())) t.expected_hosts = [...(t.expected_hosts ?? []), r.hostname];
    return { review_id: id, state: r.state };
  });
  return { results };
}

const REASONS = ['ENROLL_CLOCK_SKEW', 'DEPLOY_TOKEN_SOURCE_DENIED', 'KMGR_DPA_REQUIRED'];

function mockEnrollments(t: DeployToken): Enrollment[] {
  const out: Enrollment[] = [];
  const total = (t.progress?.approved ?? 0) + (t.progress?.failed ?? 0);
  for (let i = 0; i < total; i++) {
    const bad = i % 7 === 6;
    out.push({
      enrollment_id: i + 1,
      result: bad ? 'rejected' : 'enrolled',
      reason: bad ? REASONS[i % REASONS.length] : undefined,
      device_ref: bad ? undefined : `KMA-MOCK${String(i).padStart(12, '0')}-X`,
      model: 'kmgr-agent-linux-x64',
      hw_anchor_hash: String(i).padStart(64, '0'),
      // 被拒的電腦還沒上線，kmgr 不知道它的主機名稱與 OS
      hostname: bad ? undefined : `SRV-${String(100 + i)}`,
      os: bad ? undefined : { family: 'linux', name: 'Ubuntu', version: '24.04' },
      source_ip: '203.69.141.' + (32 + (i % 8)),
      at: iso((total - i) * 5000),
    });
  }
  return out.reverse();
}

export const kmgrMock = {
  /** 測試用：清空 mock 狀態。 */
  reset(): void {
    state = empty();
    save();
  },

  /** 回傳複本（畫面拿到的物件不能跟 mock 狀態共用，不然版本會被就地改掉）。 */
  async handle(w: MockWorld, method: string, path: string, query: Record<string, string | number | string[] | undefined>, body: unknown): Promise<unknown> {
    const r = route(w, method, path, query, body);
    return r === undefined ? undefined : JSON.parse(JSON.stringify(r));
  },
};

function route(w: MockWorld, method: string, path: string, query: Record<string, string | number | string[] | undefined>, body: unknown): unknown {
  const s = load();
  const seg = path.split('/').map(decodeURIComponent);
  // ["", "v1", "me"|"homes"|"orgs", id, "kmgr", ...]
  if (path === '/v1/me/kmgr/dashboard') {
    const scope = scopeLabel(query.scope);
    const homes = enabledHomes(s, w).filter((h) => inScopes(w, query.scope, h.home_id));
    const ids = new Set(homes.map((h) => h.home_id));
    const incs = s.incidents.map((x) => x.inc).filter((i) => ids.has(i.home_id));
    const flags = readMockFlags();
    const statuses = homes.map((h) => homeStatus(s, h, w, flags));
    const byState: Record<string, number> = {};
    for (const x of statuses) byState[x.state] = (byState[x.state] ?? 0) + 1;
    const many = statuses.length > 12;
    const allHosts = homes.flatMap((h) => mockHosts(s, h.home_id, h.org_id ?? '', flags));
    const nets = statuses.map((x) => x.network!);
    const unreadable = nets.filter((n) => n.source_state === 'unreadable');
    const d: WireDashboard = {
      scope,
      checked_at: new Date().toISOString(),
      incidents: { source_state: 'ok', counts: counts(incs, w.me), items: sorted(incs.filter((i) => i.handling !== 'resolved')).slice(0, 5) },
      homes: {
        source_state: 'ok',
        total: statuses.length,
        counts: byState,
        items: many ? statuses.filter((x) => x.state !== 'ok') : statuses,
        others_ok: many ? statuses.filter((x) => x.state === 'ok').length : 0,
      },
      hosts: { ...EMPTY_AGENTS, ...agentCounts(allHosts), source_state: 'ok', disk_full: allHosts.filter(mockDiskFull).length },
      network: homes.length
        ? {
            source_state: unreadable.length ? 'unreadable' : 'ok',
            unreadable_homes: unreadable.length,
            collectors: nets.reduce((a, n) => a + (n.collectors ?? 0), 0),
            collectors_offline: nets.flatMap((n) => n.collectors_offline ?? []),
            probes: nets.reduce((a, n) => a + (n.probes ?? 0), 0),
            probes_up: nets.reduce((a, n) => a + (n.probes_up ?? 0), 0),
            probes_failing: nets.reduce((a, n) => a + (n.probes_failing ?? 0), 0),
          }
        : undefined,
      kinio: { source_state: 'ok', homes: homes.length, unreadable: 0, devices: 18 * homes.length, channels: 5 * homes.length, cameras: 16 * homes.length, recorders: 2 * homes.length, offline_devices: homes.length, offline_channels: 0, storage_faults: 0, recording_gaps: homes.length, recording_continuous: 12 * homes.length, checked_at: new Date().toISOString() },
      global_stop: flags.global_stop ? { active: true, scope: 'all', started_at: iso(9 * MIN), reason: '懷疑有人冒用帳號，先停下全部自動處置' } : { active: false },
      names: { ...names(w), things: { ...THINGS, ...Object.fromEntries(allHosts.map((h) => [h.target_ref, h.hostname ?? ''])) } },
    };
    // 範圍含 2 個以上組織：依組織分組（M5-04）
    const orgIds = [...new Set(homes.map((h) => h.org_id ?? ''))].filter(Boolean);
    if (orgIds.length >= 2) {
      d.by_org = orgIds.map((org) => {
        const hs = homes.filter((h) => h.org_id === org);
        const hid = new Set(hs.map((h) => h.home_id));
        const open = incs.filter((i) => hid.has(i.home_id) && i.handling !== 'resolved');
        const un = open.filter((i) => i.handling === 'open' || i.handling === 'acknowledged');
        const st: Record<string, number> = {};
        for (const x of statuses.filter((x) => hid.has(x.home_id))) st[x.state] = (st[x.state] ?? 0) + 1;
        return {
          org_id: org,
          homes: hs.length,
          incidents_open: open.length,
          p1_open: open.filter((i) => i.severity === 'p1').length,
          p1_unclaimed: un.filter((i) => i.severity === 'p1').length,
          unclaimed: un.length,
          homes_by_state: st,
          hosts_offline: allHosts.filter((h) => hid.has(h.home_id ?? '') && hostGroup(h) === 'offline').length,
          hosts_disk_full: allHosts.filter((h) => hid.has(h.home_id ?? '') && mockDiskFull(h)).length,
          network: { source_state: 'unset' as const },
        };
      });
    }
    return d;
  }
  if (path === '/v1/me/kmgr/hosts') {
    const flags = readMockFlags();
    const scope = scopeLabel(query.scope);
    const homes = enabledHomes(s, w).filter((h) => inScopes(w, query.scope, h.home_id));
    let list = homes.flatMap((h) => mockHosts(s, h.home_id, h.org_id ?? '', flags));
    const c = hostCounts(list);
    if (query.state) list = list.filter((h) => hostGroup(h) === String(query.state));
    const r: WireHostList = { scope, counts: c, hosts: list, names: { ...names(w), things: Object.fromEntries(list.map((h) => [h.target_ref, h.hostname ?? ''])) } };
    return r;
  }
  if (path === '/v1/me/kmgr/incidents') {
    const scope = scopeLabel(query.scope);
    const ids = new Set(enabledHomes(s, w).filter((h) => inScopes(w, query.scope, h.home_id)).map((h) => h.home_id));
    let list = s.incidents.map((x) => x.inc).filter((i) => ids.has(i.home_id));
    const handling = query.handling ? String(query.handling).split(',') : ['open', 'acknowledged', 'claimed'];
    list = list.filter((i) => handling.includes(i.handling));
    if (query.claimed_by === 'none') list = list.filter((i) => !i.claimed_by);
    else if (query.claimed_by) list = list.filter((i) => i.claimed_by === (query.claimed_by === 'me' ? w.me : query.claimed_by));
    if (query.severity) list = list.filter((i) => String(query.severity).split(',').includes(i.severity));
    return { incidents: sorted(list), scope, names: names(w) };
  }
  if (seg[2] === 'homes' && seg[4] === 'kmgr') {
    const homeId = seg[3];
    if (seg.length === 5 && method === 'GET') {
      const h = w.homes.find((x) => x.home_id === homeId);
      if (!h) notFound();
      const st = homeStatus(s, h, w);
      const r: WireHomeKmgr = { ...st, kmgr_enabled: !!s.enabled[homeId], dpa_accepted: !!s.dpa[h.org_id ?? ''], can_configure: true, can_handle: true, can_remote: true };
      return r;
    }
    const flags = readMockFlags();
    const hostsHere = () => mockHosts(s, homeId, orgOfHome(w, homeId) ?? '', flags);
    if (seg[5] === 'targets' && seg.length === 6 && method === 'GET') {
      const kind = String(query.kind ?? 'agent');
      const hs = hostsHere();
      const thingNames = Object.fromEntries(hs.map((h) => [h.target_ref, h.hostname ?? '']));
      if (kind === 'probe') {
        const r: WireTargetList = { targets: mockProbes(s, homeId).map((p) => ({ target_ref: p.probe_id, kind: 'probe', probe: p })), names: { things: thingNames } };
        return r;
      }
      if (kind === 'agent') {
        const r: WireTargetList = { targets: hs, names: { ...names(w), things: thingNames } };
        return r;
      }
      return { targets: [] };
    }
    if (seg[5] === 'targets' && seg.length === 7 && method === 'PUT') {
      const ref = seg[6];
      if (!hostsHere().some((h) => h.target_ref === ref)) notFound();
      const b = body as { critical?: boolean; expected_offline?: TimeWindows | null };
      if (b.critical === undefined && b.expected_offline === undefined) throw new KinioError(422, 'INVALID_ARGUMENT', '至少帶一個欄位');
      s.targets[ref] = { ...(s.targets[ref] ?? {}), ...(b.critical !== undefined ? { critical: b.critical } : {}), ...(b.expected_offline !== undefined ? { expected_offline: b.expected_offline && b.expected_offline.windows.length ? b.expected_offline : null } : {}) };
      save();
      return hostsHere().find((h) => h.target_ref === ref);
    }
    if (seg[5] === 'probes') {
      const list = mockProbes(s, homeId);
      const col = hostsHere().find((h) => h.collector);
      const b = body as ProbeInput | undefined;
      const check = (x: ProbeInput) => {
        if (x.target && /^10\.99\./.test(x.target)) throw new KinioError(422, 'NETWORK_SCAN_NOT_ALLOWED', '目標不在組織允許掃的網段', { field: 'target' });
      };
      if (seg.length === 6 && method === 'POST' && b) {
        check(b);
        const p: WireProbe & { home_id: string } = { ...b, probe_id: id(s, 'prb'), home_id: homeId, collector: b.collector ?? (b.credential_ref || /^10\.20\./.test(b.target ?? '') || list.length ? col?.target_ref : undefined), updated_at: new Date().toISOString() };
        list.push(p);
        save();
        return p;
      }
      if (seg[6] === 'test' && method === 'POST' && b) {
        check(b);
        if (!col || col.presence !== 'online') throw new KinioError(503, 'UNAVAILABLE', '收集器離線', { reason: 'collector_offline' });
        if (b.kind === 'snmp' && !b.credential_ref) return { collector: col.target_ref, up: false, error_code: 'credential_missing', message: '沒有帳密' };
        if (b.target?.endsWith('.3')) return { collector: col.target_ref, up: false, error_code: 'auth_failed', message: '使用者名稱或密碼不對' };
        return { collector: col.target_ref, up: true, latency_ms: 2.4, ...(b.kind === 'snmp' ? { interfaces: 24 } : {}), ...(b.kind === 'http' ? { http_status: 200, tls_expiry_days: 88 } : {}) };
      }
      const cur = list.find((p) => p.probe_id === seg[6]);
      if (!cur) notFound();
      if (method === 'PUT' && b) {
        check(b);
        Object.assign(cur, { ...b, probe_id: cur.probe_id, home_id: homeId, last_at: undefined, last_up: undefined, last_error: undefined, updated_at: new Date().toISOString() });
        save();
        return cur;
      }
      if (method === 'DELETE') {
        list.splice(list.indexOf(cur), 1);
        save();
        return undefined;
      }
    }
    if (seg[5] === 'collectors' && seg[7] === 'assignments' && method === 'POST') {
      const dev = seg[6];
      if (!hostsHere().some((h) => h.target_ref === dev)) notFound();
      const b = body as { targets: string[] };
      for (const c of b.targets) if (c.startsWith('10.99.')) throw new KinioError(422, 'NETWORK_SCAN_NOT_ALLOWED', '網段不在組織允許範圍', { field: 'targets', target: c });
      s.assignments[homeId] = b.targets;
      save();
      return { collector: dev, assignments: b.targets.map((t) => ({ target: t, target_kind: t.includes('/') ? 'cidr' : 'network_device', collector: dev, valid_from: new Date().toISOString() })) };
    }
    if (seg[5] === 'maintenance') {
      if (method === 'PUT') {
        const b = body as { hours?: number; until?: string | null; schedule?: TimeWindows | null };
        if (b.hours !== undefined && b.until !== undefined) throw new KinioError(422, 'INVALID_ARGUMENT', 'hours 與 until 擇一', { field: 'hours' });
        if (b.hours !== undefined && (b.hours < 1 || b.hours > 72)) throw new KinioError(422, 'INVALID_ARGUMENT', '1–72 小時', { field: 'hours' });
        const cur = s.maintenance[homeId] ?? {};
        const next = { ...cur };
        if (b.hours !== undefined) Object.assign(next, { until: new Date(Date.now() + b.hours * 3600_000).toISOString(), set_by: w.me, set_at: new Date().toISOString() });
        if (b.until !== undefined) Object.assign(next, b.until === null ? { until: undefined } : { until: b.until, set_by: w.me, set_at: new Date().toISOString() });
        if (b.schedule !== undefined) next.schedule = b.schedule && b.schedule.windows.length ? b.schedule : undefined;
        const changed = JSON.stringify(cur) !== JSON.stringify(next);
        s.maintenance[homeId] = next;
        save();
        return { ...maintenanceOf(s, homeId), changed, notified: changed ? 1 : 0, names: names(w) };
      }
      return { ...maintenanceOf(s, homeId), names: names(w) };
    }
    if (seg[5] === 'business-hours') {
      if (method === 'PUT') {
        const b = body as TimeWindows;
        s.business[homeId] = { tz: b.tz || 'Asia/Taipei', windows: b.windows ?? [] };
        save();
        return { ...businessOf(s, homeId), changed: true, updated_by: w.me, updated_at: new Date().toISOString(), names: names(w) };
      }
      return businessOf(s, homeId);
    }
    if (seg[5] === 'severity-overrides') {
      if (method === 'PUT') {
        const b = body as Pick<Thresholds, 'rules' | 'disk_pct' | 'tls_days' | 'workstation_offline_days'>;
        s.thresholds[homeId] = { home_id: homeId, ...b, updated_by: { user_id: w.me, display_name: w.meName }, updated_at: new Date().toISOString() };
        save();
      }
      const th = s.thresholds[homeId] ?? defaultThresholds(homeId);
      const out: WireThresholds = { ...th, rules: th.rules.map((r) => ({ ...r, overridden: overriddenRules(th.rules).has(r.key) })), updated_by: th.updated_by?.user_id, names: names(w) };
      return out;
    }
    if (seg[5] === 'site-info') {
      if (method === 'PUT') {
        const b = body as Omit<SiteInfo, 'home_id'>;
        s.site[homeId] = { ...b, home_id: homeId, updated_by: { user_id: w.me, display_name: w.meName }, updated_at: new Date().toISOString() };
        save();
      }
      const si = s.site[homeId] ?? { home_id: homeId };
      const out: WireSiteInfo = { ...si, updated_by: si.updated_by?.user_id, names: names(w) };
      return out;
    }
    if (seg[5] === 'notify') {
      if (method === 'PUT') {
        const b = body as { schedule_id: string | null; customer_contact: string | null };
        s.notify[homeId] = { schedule_id: b.schedule_id || null, customer_contact: b.customer_contact || null };
        save();
      }
      const n = s.notify[homeId] ?? { schedule_id: null, customer_contact: null };
      const sch = s.schedules.find((x) => x.schedule_id === n.schedule_id);
      const r: HomeNotify = {
        home_id: homeId,
        ...n,
        schedule: sch ? { schedule_id: sch.schedule_id, org_id: sch.org_id, name: sch.name, tz: sch.tz } : null,
        recipients_now: sch ? { route: 'oncall', user_ids: sch.slots[0]?.rotation.slice(0, 1) ?? [] } : { route: 'home_admins', roles: ['owner', 'admin'] },
        ...(method === 'PUT' ? { changed: true, notified: 1 } : {}),
      };
      return r;
    }
    if (seg[5] === 'incidents' && seg[6]) {
      const m = s.incidents.find((i) => i.inc.home_id === homeId && i.inc.incident_id === seg[6]);
      if (!m || !s.enabled[homeId]) notFound();
      if (seg.length === 7 && method === 'GET') {
        const d: WireIncidentDetail = { incident: m.inc, alerts: m.alerts, history: m.history, notifications: m.notifications, can_handle: true, names: names(w) };
        return d;
      }
      if (seg[7] === 'state' && method === 'POST') return setState(w, m, body as { handling: WireIncident['handling']; assignee?: string; note?: string; expected_version?: number; expected_handling?: string[] });
    }
    if (seg[5] === 'deploy-tokens') {
      if (seg.length === 6 && method === 'GET') return { deploy_tokens: s.tokens.filter((t) => t.home_id === homeId).map(tokenView).reverse() };
      if (seg.length === 6 && method === 'POST') {
        const b = body as { name: string; max_count: number; expires_at?: string; cidrs?: string[]; ownership?: 'org' | 'byod'; expected_hosts?: string[] };
        const tid = id(s, 'kdt');
        const t = {
          token_id: tid,
          home_id: homeId,
          name: b.name,
          state: 'active' as const,
          expires_at: b.expires_at ?? new Date(Date.now() + 7 * 86_400_000).toISOString(),
          max_count: b.max_count,
          used_count: 0,
          cidrs: b.cidrs ?? [],
          ownership: b.ownership ?? 'org',
          expected_hosts: b.expected_hosts ?? [],
          keyset_hash: '0'.repeat(64),
          created_by: w.me,
          created_at: new Date().toISOString(),
          created_ms: Date.now(),
        };
        s.tokens.push(t);
        (s.reviews ??= {})[tid] = seedReviews(t);
        save();
        const out: CreatedDeployToken = { ...tokenView(t), token: `kdt_${tid}.MOCKSECRET${tid.slice(-6)}.${'0'.repeat(64)}` };
        return out;
      }
      const t = s.tokens.find((x) => x.home_id === homeId && x.token_id === seg[6]);
      if (!t) notFound();
      if (seg.length === 7 && method === 'GET') return tokenView(t);
      if (seg.length === 7 && method === 'DELETE') {
        t.revoked_at ??= new Date().toISOString();
        t.revoked_reason ??= 'manual';
        save();
        return tokenView(t);
      }
      if (seg[7] === 'enrollments') return { enrollments: mockEnrollments(tokenView(t)) };
      if (seg[7] === 'reviews') {
        const list = ((s.reviews ??= {})[t.token_id] ??= []);
        if (method === 'GET') {
          if (t.revoked_at || Date.parse(t.expires_at) < Date.now()) for (const r of list) if (r.state === 'pending') r.state = 'expired';
          const st = query.state;
          return { reviews: st ? list.filter((r) => r.state === st) : list };
        }
        if (method === 'POST') {
          const out = decideReviews(t, list, body as { decision: 'accept' | 'reject'; review_ids: string[] });
          save();
          return out;
        }
      }
    }
  }
  if (seg[2] === 'orgs' && seg[4] === 'kmgr') {
    const orgId = seg[3];
    if (seg.length === 5 && method === 'GET') {
      const d = s.dpa[orgId];
      const flags = readMockFlags();
      const r: WireOrgKmgr = {
        org_id: orgId,
        dpa: { current_version: DPA_VERSION, summary: [], accepted: d?.version === DPA_VERSION, accepted_version: d?.version, accepted_by: d?.by, accepted_at: d?.at },
        homes: w.homes.filter((h) => h.org_id === orgId && s.enabled[h.home_id] !== undefined).map((h) => ({ home_id: h.home_id, kmgr_enabled: !!s.enabled[h.home_id] })),
        global_stop: flags.global_stop ? { active: true, scope: 'all', started_at: iso(9 * MIN), reason: '懷疑有人冒用帳號，先停下全部自動處置' } : { active: false },
        security_policy: { org_id: orgId, remote_access: 'attended', auto_actions: 'off', network_scan: ['10.20.0.0/16'], activity_monitoring: 'off' },
        names: names(w),
      };
      return r;
    }
    if (seg[5] === 'security-policy' && method === 'GET') return { org_id: orgId, remote_access: 'attended', auto_actions: 'off', network_scan: ['10.20.0.0/16'], activity_monitoring: 'off' };
    if (seg[5] === 'seats') {
      const flags = readMockFlags();
      if (seg.length === 6 && method === 'GET') return orgSeats(s, w, orgId, flags);
      if (seg.length === 7 && method === 'PUT') {
        const cur = orgSeats(s, w, orgId, flags);
        const seat = cur.seats?.find((x) => x.seat_id === seg[6]);
        if (!seat) notFound();
        const b = body as { assigned_ref?: string; class?: 'server' | 'workstation' };
        if (b.assigned_ref === undefined && !b.class) throw new KinioError(422, 'INVALID_ARGUMENT', '至少帶一個', { field: 'assigned_ref' });
        const map = (s.seats[orgId] ??= Object.fromEntries((cur.seats ?? []).map((x) => [x.seat_id, x.assigned_ref])));
        if (b.assigned_ref !== undefined) {
          // 同一個交易：先解除原本持有這台的那一席
          for (const k of Object.keys(map)) if (b.assigned_ref && map[k] === b.assigned_ref) map[k] = undefined;
          map[seat.seat_id] = b.assigned_ref || undefined;
        }
        save();
        const after = orgSeats(s, w, orgId, flags);
        return { seat: after.seats?.find((x) => x.seat_id === seg[6]) };
      }
    }
    if (seg[5] === 'enable' && method === 'POST') {
      const b = body as { dpa_version: string; home_ids: string[] };
      if (b.dpa_version !== DPA_VERSION) throw new KinioError(422, 'INVALID_ARGUMENT', '協議版本不是目前版本', { field: 'dpa_version', current: DPA_VERSION });
      s.dpa[orgId] = { version: b.dpa_version, by: w.me, at: new Date().toISOString() };
      for (const h of b.home_ids) {
        s.enabled[h] = true;
        seedIncidents(s, w, h);
      }
      save();
      const d = s.dpa[orgId];
      return { org_id: orgId, dpa_version: d.version, dpa_accepted_by: d.by, dpa_accepted_at: d.at, homes: b.home_ids.map((h) => ({ home_id: h, kmgr_enabled: true })) };
    }
    if (seg[5] === 'oncall-schedules') {
      const withCount = (x: OncallSchedule): OncallSchedule => ({ ...x, home_count: Object.values(s.notify).filter((n) => n.schedule_id === x.schedule_id).length });
      if (seg.length === 6 && method === 'GET') return { org_id: orgId, schedules: s.schedules.filter((x) => x.org_id === orgId).map(withCount) };
      const b = body as Omit<OncallSchedule, 'schedule_id' | 'org_id' | 'version'> & { expected_version?: number };
      if (seg.length === 6 && method === 'POST') {
        if (b.expected_version !== undefined) throw new KinioError(422, 'INVALID_ARGUMENT', '新增不帶 expected_version', { field: 'expected_version' });
        const sch: OncallSchedule = { name: b.name, tz: b.tz, supervisor: b.supervisor, slots: b.slots, rotation_start: b.rotation_start, schedule_id: id(s, 'onc'), org_id: orgId, version: 1 };
        s.schedules.push(sch);
        save();
        return sch;
      }
      const cur = s.schedules.find((x) => x.org_id === orgId && x.schedule_id === seg[6]);
      if (!cur) notFound();
      if (method === 'GET') return withCount(cur);
      if (method === 'PUT') {
        if (b.expected_version === undefined) throw new KinioError(400, 'BAD_REQUEST', 'PUT 要帶 expected_version', { field: 'expected_version' });
        if (b.expected_version !== cur.version) throw new KinioError(409, 'CONFLICT', '輪值表已被別人改過', { field: 'expected_version', current_version: cur.version });
        Object.assign(cur, { name: b.name, tz: b.tz, supervisor: b.supervisor, slots: b.slots, rotation_start: b.rotation_start, version: cur.version + 1 });
        save();
        return cur;
      }
    }
  }
  throw new KinioError(404, 'NOT_FOUND', 'mock 沒有這個路徑');
}

const TRANSITIONS: Record<string, string[]> = { open: ['acknowledged', 'claimed', 'resolved'], acknowledged: ['claimed', 'resolved'], claimed: ['claimed', 'resolved'], resolved: [] };

function setState(w: MockWorld, m: MockIncident, b: { handling: WireIncident['handling']; assignee?: string; note?: string; expected_version?: number; expected_handling?: string[] }): unknown {
  const inc = m.inc;
  if (inc.handling === b.handling && (b.handling !== 'claimed' || inc.claimed_by === (b.assignee || w.me))) return { incident: inc, changed: false };
  const stale = b.expected_version !== undefined ? b.expected_version !== inc.version : b.expected_handling ? !b.expected_handling.includes(inc.handling) : false;
  if (stale) {
    const by = inc.handling === 'claimed' ? inc.claimed_by : inc.handling === 'acknowledged' ? inc.acknowledged_by : inc.resolved_by;
    throw new KinioError(409, 'INCIDENT_STATE_CONFLICT', '事故狀態已經變了', { current: inc.handling, version: inc.version, ...(by ? { handled_by: by } : {}) });
  }
  if (!TRANSITIONS[inc.handling].includes(b.handling)) throw new KinioError(422, 'INVALID_TRANSITION', '不能從目前的狀態這樣改', { from: inc.handling, to: b.handling });
  const at = new Date().toISOString();
  const from = inc.handling;
  inc.handling = b.handling;
  if (b.handling === 'claimed') {
    inc.claimed_by = b.assignee || w.me;
    inc.claimed_at = at;
  }
  if (b.handling === 'acknowledged') {
    inc.acknowledged_by = w.me;
    inc.acknowledged_at = at;
  }
  if (b.handling === 'resolved') {
    inc.resolved_by = w.me;
    inc.resolved_at = at;
  }
  inc.version += 1;
  m.history.push({ version: inc.version, from, handling: b.handling, now_state: inc.now_state, cause: 'user', by: w.me, assignee: b.assignee, note: b.note, at });
  save();
  return { incident: inc, changed: true };
}

/** mock 的「磁碟快滿」（kmgr disk_full 告警還開著）：有一顆磁碟 > 90%。 */
function mockDiskFull(h: { metrics?: { disks?: { used_pct: number }[] } }): boolean {
  return (h.metrics?.disks ?? []).some((d) => d.used_pct > 90);
}
